import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePointsEnabled } from '../hooks/usePointsEnabled';

interface ProtectedRouteProps {
	children: React.ReactNode;
	requireRole?: 'admin' | 'employee' | 'superadmin';
	// Redirects to /dashboard when the company has rewards/points turned off,
	// unless the user is an admin (admins can always reach rewards pages —
	// same convention as the "Rewards" nav item in Sidebar.tsx).
	requirePointsEnabled?: boolean;
}

/**
 * Protected Route - requires authentication and content rights acceptance.
 * Waits for the startup auth check (token refresh) before making a routing decision.
 * Redirects to /login if not authenticated.
 * Redirects to /accept-terms if content rights not accepted (employees only).
 * When requireRole is set, redirects to /dashboard if the user's role doesn't match.
 */
export default function ProtectedRoute({ children, requireRole, requirePointsEnabled }: ProtectedRouteProps) {
	const location = useLocation();
	const { isAuthenticated, user } = useAuth();
	const pointsEnabled = usePointsEnabled();
	const isPrivileged = user?.role === 'admin' || user?.role === 'superadmin';

	// Note: the startup auth check (isLoading) is handled by AuthGate in App.tsx,
	// so by the time this renders the refresh has already resolved.
	if (!isAuthenticated) {
		return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
	}

	if (requireRole && user?.role !== requireRole) {
		return <Navigate to='/dashboard' replace />;
	}

	if (requirePointsEnabled && !isPrivileged && !pointsEnabled) {
		return <Navigate to='/dashboard' replace />;
	}

	const hasAcceptedContentRights = user?.hasAcceptedContentRights === true;
	const isOnAcceptTermsPage = location.pathname === '/accept-terms';

	if (!hasAcceptedContentRights && !isOnAcceptTermsPage && !isPrivileged) {
		return <Navigate to='/accept-terms' replace />;
	}

	return <>{children}</>;
}
